/* eslint-disable jsx-a11y/control-has-associated-label */
import {
  CURRENCY_VS_SYMBOL_MAP,
  formatNumberWithUniqueCommaPattern,
  SerializedStockClass,
} from '@commenda/utils';
import { TrashIcon } from '@heroicons/react/20/solid';
import { Currency, StockClassType } from '@prisma/client';
import useModal from '../../../hooks/useModal';
import ConfirmDeleteModal from '../../modal/confirm-delete-modal';

const DeleteSection = ({
  id,
  onDelete,
}: {
  id: string;
  onDelete: (id: string) => void;
}) => {
  const {
    isShown: isDeleteStockClassModalShown,
    toggle: toggleDeleteStockClassModalVisibility,
  } = useModal();

  return (
    <div>
      <ConfirmDeleteModal
        isDeleteModalShowed={isDeleteStockClassModalShown}
        toggleDeleteModal={toggleDeleteStockClassModalVisibility}
        headerText="Really delete this stock class?"
        modalMessage="This action cannot be reversed."
        onDelete={() => onDelete(id)}
      />
      <TrashIcon
        className="h-6 w-6 text-gray-400 hover:text-red-500 cursor-pointer"
        aria-hidden="true"
        onClick={toggleDeleteStockClassModalVisibility}
      />
    </div>
  );
};

const StockClassTable = ({
  stockClasses,
  currency,
  onDelete,
}: {
  onDelete: (id: string) => void;
  stockClasses: SerializedStockClass[];
  currency: Currency;
}) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flow-root w-full">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Class
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Authorized Shares
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Per share price
                    </th>

                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Outstanding Shares
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {stockClasses?.map((stockClass) => {
                    return (
                      <tr key={stockClass.name}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {stockClass.type === StockClassType.PREFERRED
                            ? 'Preferred'
                            : 'Common'}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {stockClass.name}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {formatNumberWithUniqueCommaPattern(
                            stockClass.authorizedShares
                          )}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {
                            stockClass?.additionalInformation?.pricePerShare
                              ?.amount
                          }
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {formatNumberWithUniqueCommaPattern(
                            stockClass.outstandingShares
                          )}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          {CURRENCY_VS_SYMBOL_MAP[currency]}
                          {formatNumberWithUniqueCommaPattern(
                            stockClass.amountRaised,
                            5
                          )}
                        </td>
                        <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                          <DeleteSection
                            id={stockClass.id}
                            onDelete={onDelete}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockClassTable;
